import React, { useEffect, useState, useContext, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { TradesNeeded } from './TradesNeeded';
import { TradesInitiated } from './TradesInitiated';
import { TradesScheduled } from './TradesScheduled';
import { TradesBounced } from './TradesBounced';
import { AddSpeakeasyJob } from './AddSpeakeasyJob';
import { useSpeakeasyTrades } from '../../modules/Trades/SpeakeasyTrades';
import { AppContext } from '../../modules/AppContext';
import { SpeakeasyJobModal } from './SpeakeasyJobModal';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import config from '../../config';
import { HUBBLE_SESSION_AUTH } from '../../modules/Login';
import Session from '../../modules/Session';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
}));

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

export const SpeakeasyTrades = () => {
  const { searchToken } = useContext(AppContext);
  const classes = useStyles();
  const tradesObj = useSpeakeasyTrades();
  const [tradesLoading, setTradesLoading] = useState(false);
  const [jobDrilldownModalOpen, setJobDrilldownModalOpen] = useState(false);
  const [jobDrilldownData, setJobDrilldownData] = useState(null);
  const [connection, setConnection] = useState(null);
  const [sendingSms, setSendingSms] = useState(false);
  const [tradesProcessing, setTradesProcessing] = useState([]);
  const [dataVersion, setDataVersion] = useState(0);

  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [toastIsVisible, setToastIsVisible] = useState(false);
  const [toastDuration, setToastDuration] = useState(6000);

  const showToast = ({ message, type, duration }) => {
    setToastMessage(message);
    setToastType(type);
    setToastIsVisible(true);
    setToastDuration(duration || 6000);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setToastIsVisible(false);
  };

  const tradeData = useRef({});
  const searchedTradeData = useRef({});

  const setTradeData = (data) => {
    tradeData.current = data;
  };
  const setSearchedTradeData = (data) => {
    searchedTradeData.current = data;
    setDataVersion((prev) => prev + 1);
  };

  const searchableColumns = [
    'advisorName',
    'participantName',
    'participantId',
    'type',
    'status',
    'product',
  ];

  const jobStatusesToCategory = {
    pending: 'pending',
    active: 'inProgress',
    readyForCode: 'inProgress',
    codeReceived: 'inProgress',
    completed: null,
    scheduled: 'scheduled',
    expired: null,
    annulled: null,
    bounced: 'bounced',
    scheduling: null,
  };

  const applyJobUpdate = (jobUpdate) => {
    const tradeDataWithOldJobRemoved = {};
    Object.keys(tradeData.current).forEach((category) => {
      tradeDataWithOldJobRemoved[category] = (tradeData.current[category] || []).filter(
        (job) => String(job.id) !== String(jobUpdate.id)
      );
    });

    const newJobCategory = jobStatusesToCategory[jobUpdate.status];
    if (newJobCategory) {
      const newTradeData = {
        ...tradeDataWithOldJobRemoved,
        [newJobCategory]: [
          ...(tradeDataWithOldJobRemoved[newJobCategory] || []),
          jobUpdate,
        ],
      };
      setTradeData(newTradeData);
      setSearchedTradeData(newTradeData);
    } else {
      setTradeData(tradeDataWithOldJobRemoved);
      setSearchedTradeData(tradeDataWithOldJobRemoved);
    }
  };

  const searchTrades = () => {
    let newTrades = { pending: [], inProgress: [], scheduled: [] };
    if (searchToken.length > 0) {
      const filterObject = (row) => {
        const matches = searchableColumns.filter((col) => {
          const field = row[col] || '';
          const isMatch = String(field)
            .toLowerCase()
            .includes(String(searchToken).toLowerCase());
          return isMatch;
        });
        return matches.length > 0;
      };
      newTrades.pending = tradeData.current?.pending?.filter(filterObject);
      newTrades.inProgress =
        tradeData.current?.inProgress?.filter(filterObject);
      newTrades.scheduled = tradeData.current?.scheduled?.filter(filterObject);
      newTrades.bounced = tradeData.current?.bounced?.filter(filterObject);
    } else {
      newTrades = tradeData.current;
    }
    setSearchedTradeData(newTrades);
  };

  const getTrades = () => {
    setTradesLoading(true);
    tradesObj
      .getTrades()
      .then(async (res) => {
        if (res.ok) {
          let data = [];
          try {
            data = (await res.json()) || [];
          } catch (err) {
            console.error(err);
            showToast({
              message: `Error fetching jobs:: ${err}`,
              type: 'error',
            });
          }
          setTradeData(data);
          setSearchedTradeData(data);
        } else {
          showToast({
            message: `Error fetching jobs`,
            type: 'error',
          });
        }
      })
      .finally(() => {
        setTradesLoading(false);
      });
  };

  const sendInvite = (args) => {
    setSendingSms(true);
    tradesObj.sendInvite(args).then(async (res) => {
      if (res.ok) {
        showToast({
          message: `SMS sent successfully!`,
          type: 'success',
        });
      } else {
        try {
          showToast({
            message: `Error sending SMS: ${(await res.text()) || ''}`,
            type: 'error',
          });
        } catch (err) {
          console.error(err);
        }
      }
      setSendingSms(false);
    });
  };

  const sendCodeExchangeInvite = (args) => {
    setSendingSms(true);
    tradesObj.sendCodeExchangeInvite(args).then(async (res) => {
      if (res.ok) {
        showToast({
          message: `Code exchange SMS sent successfully!`,
          type: 'success',
        });
      } else {
        try {
          showToast({
            message: `Error sending code exchange SMS: ${
              (await res.text()) || ''
            }`,
            type: 'error',
          });
        } catch (err) {
          console.error(err);
        }
      }
      setSendingSms(false);
    });
  };

  const updateStatus = (jobId, status) => {
    setTradesProcessing((prev) => [...prev, jobId]);
    tradesObj.updateStatus(jobId, status).then(async (res) => {
      if (res.ok) {
        showToast({
          message: `Job updated successfully!`,
          type: 'success',
        });

        const existingJob = Object.values(tradeData.current)
          .flat()
          .find((job) => String(job.id) === String(jobId));

        let nextStatus = status;
        try {
          const body = await res.json();
          if (body?.status) {
            nextStatus = body.status;
          }
        } catch (err) {
          console.error(err);
        }

        if (existingJob) {
          const leftReadyForCode =
            existingJob.status === 'readyForCode' && nextStatus === 'active';
          applyJobUpdate({
            ...existingJob,
            status: nextStatus,
            expirationCount: leftReadyForCode
              ? (existingJob.expirationCount || 0) + 1
              : existingJob.expirationCount,
            expires: leftReadyForCode ? null : existingJob.expires,
          });
        }

        setTradesProcessing((prev) => prev.filter((id) => id !== jobId));
      } else {
        try {
          showToast({
            message: `Error updating status: ${(await res.text()) || ''}`,
            type: 'error',
          });
        } catch (err) {
          console.error(err);
        }
        setTradesProcessing((prev) => prev.filter((id) => id !== jobId));
      }
    });
  };

  const createJob = (participantId, type) => {
    tradesObj.createJob(participantId, type).then(async (res) => {
      if (res.ok) {
        showToast({
          message: `Job created successfully!`,
          type: 'success',
        });
      } else {
        try {
          showToast({
            message: `Error creating job: ${(await res.text()) || ''}`,
            type: 'error',
          });
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  const toggleJobModal = (jobId) => {
    setJobDrilldownModalOpen((open) => !open);
    if (jobDrilldownModalOpen) {
      setJobDrilldownData({});
    } else {
      setJobDrilldownData(
        Object.values(tradeData.current)
          .flat()
          .find((job) => job.id === jobId)
      );
    }
  };

  useEffect(() => {
    getTrades();
  }, []);

  useEffect(() => {
    searchTrades();
  }, [searchToken]);

  // When ReadyForCode jobs hit Expires, SignalR often doesn't refresh this page —
  // poll status until the server sweeper moves them back to Active.
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const jobs = tradeData.current?.inProgress || [];

    jobs.forEach((job) => {
      if (job.status !== 'readyForCode' || !job.expires) {
        return;
      }

      const msUntilExpiry = new Date(job.expires).getTime() - Date.now();
      const startPollingIn = Math.max(msUntilExpiry, 0);

      const timer = setTimeout(() => {
        let attemptsLeft = 15;
        const poll = async () => {
          if (cancelled) {
            return;
          }

          try {
            const res = await tradesObj.getJobStatus(job.id);
            if (!res.ok) {
              return;
            }
            const body = await res.json();
            const nextStatus = body?.status;
            if (nextStatus && nextStatus !== 'readyForCode') {
              const existingJob = Object.values(tradeData.current)
                .flat()
                .find((j) => String(j.id) === String(job.id));
              if (existingJob) {
                applyJobUpdate({
                  ...existingJob,
                  status: nextStatus,
                  expires:
                    nextStatus === 'active' ? null : existingJob.expires,
                  expirationCount:
                    nextStatus === 'active' &&
                    existingJob.status === 'readyForCode'
                      ? (existingJob.expirationCount || 0) + 1
                      : existingJob.expirationCount,
                });
              }
              return;
            }
          } catch (err) {
            console.error(err);
          }

          attemptsLeft -= 1;
          if (!cancelled && attemptsLeft > 0) {
            timers.push(setTimeout(poll, 5000));
          }
        };

        poll();
      }, startPollingIn);

      timers.push(timer);
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [dataVersion]);

  // either serilaize
  useEffect(() => {
    const connectToSignalR = async () => {
      try {
        const newConnection = new HubConnectionBuilder()
          .withUrl(config.pronvest.signalR.speakeasyHubUrl, {
            accessTokenFactory: () => {
              const { token } = Session.get(HUBBLE_SESSION_AUTH) || {};
              return token;
            },
          })
          .configureLogging(LogLevel.Information)
          .withAutomaticReconnect()
          .build();

        await newConnection.start();
        console.log('Connected to speakeasy hub!');

        setConnection(newConnection);

        newConnection.on('UpdateJob', (jobUpdate) => {
          applyJobUpdate(jobUpdate);
        });

        newConnection.onreconnecting((error) => {
          const msg = `Connection lost. Attempting to reconnect...`;
          console.error(`${msg} :: ${error}`);
          showToast({
            message: msg,
            type: 'error',
            duration: 50000,
          });
        });

        newConnection.onreconnected(() => {
          const msg = 'Connection reestablished.';
          console.info(msg);
          showToast({
            message: msg,
            type: 'success',
          });
        });

        newConnection.onclose((error) => {
          const msg = `Connection lost. Please refresh the page to restart the connection.`;
          console.error(msg);
          showToast({
            message: msg,
            type: 'error',
            duration: 600000,
          });

          window.location.reload();
        });
      } catch (error) {
        console.error('Error connecting to Speakeasy hub:', error);
      }
    };

    connectToSignalR();

    // Cleanup on component unmount
    return () => {
      if (connection) {
        connection.stop();
        console.log('Disconnected from speakeasy hub');
      }
    };
  }, []);

  useEffect(() => {
    if (jobDrilldownData) {
      var newJob = Object.values(searchedTradeData.current)
        .flat()
        .find((job) => job.id === jobDrilldownData.id);
      if (newJob) {
        setJobDrilldownData(newJob);
      }
    }
  }, [searchedTradeData.current]);

  return (
    <>
      <SpeakeasyJobModal
        open={jobDrilldownModalOpen}
        handleClose={() => {
          setJobDrilldownModalOpen(false);
          setJobDrilldownData(null);
        }}
        {...jobDrilldownData}
      />
      <div className={classes.wrapper}>
        <TradesNeeded
          tradesLoading={tradesLoading}
          data={searchedTradeData.current.pending || []}
          sendSMS={sendInvite}
          sendingSms={sendingSms}
          sendCodeExchangeSMS={sendCodeExchangeInvite}
          updateStatus={updateStatus}
          toggleJobModal={toggleJobModal}
        />
        <TradesInitiated
          tradesLoading={tradesLoading}
          data={searchedTradeData.current.inProgress || []}
          updateStatus={updateStatus}
          toggleJobModal={toggleJobModal}
          tradesProcessing={tradesProcessing}
          sendCodeExchangeSMS={sendCodeExchangeInvite}
          sendingSms={sendingSms}
        />
        <TradesScheduled
          tradesLoading={tradesLoading}
          data={searchedTradeData.current.scheduled || []}
          sendSMS={sendInvite}
          sendingSms={sendingSms}
          sendCodeExchangeSMS={sendCodeExchangeInvite}
          updateStatus={updateStatus}
          toggleJobModal={toggleJobModal}
          tradesProcessing={tradesProcessing}
        />
        <TradesBounced
          tradesLoading={tradesLoading}
          data={searchedTradeData.current.bounced || []}
        />
        <AddSpeakeasyJob createJob={createJob} />
      </div>
      <Snackbar
        open={toastIsVisible}
        autoHideDuration={toastDuration}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={toastType}>
          {toastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
